import Delete from '@mui/icons-material/Delete'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { Suspense, useContext, useEffect, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { components } from 'react-select'
import { MyPlanPhaseDayItemContainer } from 'src/consumer/components/MyPlansPage/MyPlanStyles'
import {
  UAPDayContentDivider,
  UAPDayContentDividerContainer,
  UAPDayHeading,
  UAPDayHeadingContainer,
  UAPDayItemEventDetailsContainer,
  UAPDayItemEventDetailsSubtitle,
  UAPDayItemEventDetailsTitle,
  UAPDayItemEventParent,
  UAPDayItemEventsContainer,
  UAPTimePlanTitlesAndCheckBoxContainer,
  UserActivityPlanPhaseDayItemContainer,
} from 'src/consumer/components/UserActivityPage/MyPlanStyles'
import {
  CustomInput,
} from 'src/consumer/components/helperComponents'
import {
  ConfirmationModal,
  CustomAsyncPaginate,
} from 'src/consumer/components/helperComponents/dynamic'
import {
  ConfirmationModalPrimaryButton,
  ConfirmationModalSecondaryButton,
} from 'src/consumer/components/helperComponentsStyles'
import {
  array_move,
  checkAndGetTargetValue,
  defaultConfirmationModalProps,
} from 'src/consumer/helpers/utilFunctions'
import { useScrollTo } from 'src/customHooks/customHooks'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import {
  ACTIVITY_OBJECT,
  fetchListOfActivities,
  fetchSingleActivity,
  returnLoadOptions,
} from '../utils'
import ActivityComponent from './ActivityComponent'
import {
  ActivityContainer,
  DayWisePlannedActivitySummary,
  PhaseLegendDiv,
  PhaseLegendVerticalDiv,
  PhaseLegendsContainer,
  PhaseSectionSubTitle,
  PlannedActivitiesTabsContainer,
  StyledContentEditPaper,
} from './styledComponents'
import PictureUploadComponent from 'src/consumer/components/Helpers/PictureUploadComponent/PictureUploadComponent'

function ActivityValue(props) {
  return (
    <components.SingleValue {...props}>
      <span>
        {props.data?.customCategoryTitle} - <strong>{props.data?.activityTitle}</strong>
      </span>
    </components.SingleValue>
  )
}
function ActivityOption(props) {
  return (
    <components.Option {...props}>
      <span>
        {props.data?.customCategoryTitle ||
          props.selectProps.allCategories?.find((cat) => cat?.id === props.data?.metaCategoryId)
            ?.singleTitle}{' '}
        - <strong>{props.data?.activityTitle}</strong>
      </span>
    </components.Option>
  )
}

function SortablePlannedActivityItem({
  pAct,
  idx,
  disabled,
  totalDays,
  validationObject,
  handleClickPlannedActivity,
  masterExpand,
  currentPhase,
  dropDownData,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `plannedActivity-${idx}`,
    disabled: disabled,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <DayWisePlannedActivitySummary
        {...(disabled ? {} : { ...attributes, ...listeners })}
        disabled={idx >= totalDays}
        isDragging={isDragging}
        style={{ opacity: idx >= totalDays ? 0.5 : 1 }}
        invalid={
          idx < totalDays &&
          validationObject?.plannedActivities?.[idx]?.valid === false
        }
        onClick={() => {
          handleClickPlannedActivity(idx)
        }}
      >
        <PlannedActivityPreviewComponent
          masterExpand={masterExpand}
          plannedActivity={pAct}
          idx={idx}
          currentPhase={currentPhase}
          dropDownData={dropDownData}
        />
      </DayWisePlannedActivitySummary>
    </div>
  )
}

export default function PlanDayWiseDetailsTable({
  plannedActivities,
  setPlannedActivities,
  setCustomPlan,
  allPhases,
  disabled,
  totalDays,
  validationObject,
  dropDownData,
}) {
  const [masterExpand, setMasterExpand] = useState(true)
  const [selectedPlannedActivityIndex, setSelectedPlannedActivityIndex] = useState(0)
  const [ref, shouldScrollTo] = useScrollTo({ offset: 100 })
  const [showEditContent, setShowEditContent] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  function handleDragEnd(event) {
    const { active, over } = event

    if (over && active.id !== over.id && !disabled) {
      const oldIndex = parseInt(active.id.replace('plannedActivity-', ''), 10)
      const newIndex = parseInt(over.id.replace('plannedActivity-', ''), 10)

      if (!isNaN(oldIndex) && !isNaN(newIndex) && oldIndex !== newIndex) {
        setCustomPlan((prev) => {
          return {
            ...prev,
            plannedActivities: array_move(
              [...(prev?.plannedActivities || [])],
              oldIndex,
              newIndex,
            ),
          }
        })
      }
    }
  }

  function toggleMasterExpand() {
    setMasterExpand((prev) => !prev)
  }

  function handleClickPlannedActivity(idx) {
    setSelectedPlannedActivityIndex(idx)
    setShowEditContent(true)
  }

  function handleClosePlannedActivityEditDialog() {
    setShowEditContent(false)
  }

  useEffect(() => {
    shouldScrollTo(true)
  }, [selectedPlannedActivityIndex])

  return (
    <>
      <div className="w-100 d-flex justify-content-start align-items-center">
        <div className=" d-flex flex-column justify-content-between align-items-center">
          {/* <PhaseSectionTitle className="pb-2">Day wise planned activities</PhaseSectionTitle> */}
          <PhaseSectionSubTitle className="pb-2">
            <strong>
              {disabled
                ? 'Click on the Planned Activity to view its details'
                : 'Edit or Reorder the Planned Activities'}
            </strong>
          </PhaseSectionSubTitle>
        </div>
      </div>
      <PhaseLegendsContainer className="py-3 d-flex justify-content-start align-items-center gap-2">
        {allPhases?.map((phase, index) => (
          <PhaseLegendDiv legendColor={phase.legendColor} key={`phase-leg-${index}`}>
            {phase?.title || <em>No phase title</em>}
          </PhaseLegendDiv>
        ))}
      </PhaseLegendsContainer>
      <div className="d-flex w-100">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={plannedActivities?.map((_, idx) => `plannedActivity-${idx}`) || []}
            strategy={horizontalListSortingStrategy}
          >
            <PlannedActivitiesTabsContainer>
              {plannedActivities?.map((pAct, idx) => (
                <SortablePlannedActivityItem
                  key={idx}
                  pAct={pAct}
                  idx={idx}
                  disabled={disabled}
                  totalDays={totalDays}
                  validationObject={validationObject}
                  handleClickPlannedActivity={handleClickPlannedActivity}
                  masterExpand={masterExpand}
                  currentPhase={currentPlannedActivityPhase(idx, allPhases)}
                  dropDownData={dropDownData}
                />
              ))}
            </PlannedActivitiesTabsContainer>
          </SortableContext>
        </DndContext>
      </div>
      <Dialog
        PaperComponent={StyledContentEditPaper}
        open={showEditContent}
        onClose={handleClosePlannedActivityEditDialog}
      >
        <PlannedActivity
          plannedActivity={plannedActivities?.[selectedPlannedActivityIndex]}
          index={selectedPlannedActivityIndex}
          setPlannedActivities={setPlannedActivities}
          totalDays={totalDays}
          disabled={disabled}
          validationObject={validationObject}
          masterExpand={masterExpand}
          dropDownData={dropDownData}
          closeDialog={handleClosePlannedActivityEditDialog}
        />
      </Dialog>
    </>
  )
}

function PlannedActivity({
  plannedActivity: mainPlannedActivity,
  setPlannedActivities: setPlannedActivityWithIndex,
  totalDays = 0,
  index = 0,
  disabled,
  dragHandleProps,
  validationObject,
  masterExpand,
  dropDownData,
  closeDialog,
}) {
  const [plannedActivity, setPlannedActivity] = useState()
  const [confirmationModalProps, setConfirmationModalProps] = useState(
    defaultConfirmationModalProps,
  )

  const allActivities = plannedActivity?.otherActivities || []
  const expanded = true

  // const setPlannedActivities = useCallback(
  //   (e) => {
  //     setPlannedActivityWithIndex(e, index)
  //   },
  //   [setPlannedActivityWithIndex, index],
  // )

  function handleSubmit() {
    console.log('submit')
    setConfirmationModalProps((prev) => ({
      action: 'update',
      body: 'Are you sure you want to set this planned activity?',
      buttonColor: 'success',
      onSubmitFunctions: [
        () => {
          setPlannedActivityWithIndex((prev) => plannedActivity, index)
          closeDialog()
        },
      ],
      visibility: true,
      visibilitySetter: setConfirmationModalProps,
    }))
  }

  const setPlannedActivities = (eventPrev) => {
    setPlannedActivity((prev) => {
      if (typeof eventPrev === 'function') {
        return eventPrev(prev)
      } else {
        return {
          ...prev,
          [eventPrev.target.id]: checkAndGetTargetValue(eventPrev),
        }
      }
    })
  }

  function addActivity() {
    setPlannedActivities((prev) => ({
      ...prev,
      otherActivities: [...prev?.otherActivities, ACTIVITY_OBJECT],
    }))
  }

  function removeActivity(idx) {
    setPlannedActivities((prev) => ({
      ...prev,
      otherActivities: prev?.otherActivities?.filter((act, actIdx) => actIdx !== idx),
    }))
  }

  useEffect(() => {
    console.log({ plannedActivity })
  }, [plannedActivity])

  useEffect(() => {
    setPlannedActivity(mainPlannedActivity)
  }, [mainPlannedActivity])

  return (
    <>
      <DialogTitle>
        {/* <div className="d-flex justify-content-start align-items-center gap-3">
          <h3>Day {index + 1}</h3>
        </div> */}
        <UserActivityPlanPhaseDayItemContainer>
          <PlannedActivityPreviewComponent
            masterExpand={true}
            plannedActivity={plannedActivity}
            idx={index}
            dropDownData={dropDownData}
          />
        </UserActivityPlanPhaseDayItemContainer>
      </DialogTitle>
      <DialogContent>
        <div>
          {allActivities?.map((act, idx) => (
            <div key={idx}>
              <FormActivityComponent
                activity={act}
                setPlannedActivities={setPlannedActivities}
                idx={idx}
                removeActivity={removeActivity}
                totalActivities={allActivities.length}
                expanded={expanded}
                disabled={disabled}
                day={index + 1}
                dropDownData={dropDownData}
              >
                {' '}
                <CustomInput
                  id={'notificationText'}
                  value={plannedActivity?.notificationText}
                  label={'Notification Text'}
                  onChange={setPlannedActivities}
                  disabled={disabled}
                  placeholder={'Enter the notification text'}
                  colNumber={12}
                />
              </FormActivityComponent>
              <hr style={{ marginTop: '10px' }}></hr>
            </div>
          ))}
        </div>
        {allActivities?.length > 0 && (
          <div
            style={{
              padding: '0px 25px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            <PictureUploadComponent
              value={plannedActivity?.coverIcon}
              onChange={(event) =>
                setPlannedActivities({ target: { id: 'coverImage', value: event.target.value } })
              }
              title="Landscape Image"
              disabled={disabled}
              aspectHeight={410}
              aspectWidth={720}
            />
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <ConfirmationModalSecondaryButton onClick={closeDialog} variant="contained">
          Close
        </ConfirmationModalSecondaryButton>
        {!disabled && (
          <ConfirmationModalPrimaryButton onClick={handleSubmit} variant="contained">
            Set
          </ConfirmationModalPrimaryButton>
        )}
        {/* {!disabled && (
          <Button onClick={addActivity} variant="contained">
            Add Activity
          </Button>
        )} */}
      </DialogActions>
      <Suspense fallback={null}>
        <ConfirmationModal {...confirmationModalProps} newImplementation />
      </Suspense>
    </>
  )
}

function FormActivityComponent({
  activity,
  setPlannedActivities,
  idx = 0,
  disabled,
  removeActivity,
  totalActivities,
  expanded,
  day,
  dropDownData,
  children,
}) {
  const { mentorProfileData } = useContext(MentorProfileContext)

  const [settingExistingActivity, setSettingExistingActivity] = useState(false)
  const [confirmationModalProps, setConfirmationModalProps] = useState(
    defaultConfirmationModalProps,
  )

  function setActivity(actPrev) {
    if (typeof actPrev === 'function') {
      setPlannedActivities(
        (prev) => ({
          ...prev,
          otherActivities: prev?.otherActivities.map((act, actIndex) => {
            return actIndex === idx ? actPrev(act) : act
          }),
        }),
        idx,
      )
    } else {
      setPlannedActivities(
        (prev) => ({
          ...prev,
          otherActivities: prev?.otherActivities.map((act, actIndex) => {
            return actIndex === idx ? actPrev : act
          }),
        }),
        idx,
      )
    }
  }

  function handleDelete() {
    setConfirmationModalProps((prev) => ({
      action: 'delete',
      body: 'Are you sure you want to delete this activity?',
      buttonColor: 'error',
      onSubmitFunctions: [
        () => {
          removeActivity(idx)
        },
      ],
      visibility: true,
      visibilitySetter: setConfirmationModalProps,
    }))
  }

  function handleChangeActivity(eventOrPrev) {
    if (typeof eventOrPrev === 'function') {
      setActivity(eventOrPrev, idx)
    } else {
      setActivity(
        (prev) => ({
          ...prev,
          [eventOrPrev.target.id]: checkAndGetTargetValue(eventOrPrev),
        }),
        idx,
      )
    }
  }

  async function loadActivityOptions(inputValue, callback, { page, size }) {
    setSettingExistingActivity(true)
    // tempCard.type === 'course' ? cardLanguages : languages,
    const resp = await fetchListOfActivities(page, inputValue, mentorProfileData?.id)
    let dropDownOptions = { ...resp }
    setSettingExistingActivity(false)
    const formedReturnData = returnLoadOptions(dropDownOptions)
    return formedReturnData
  }

  async function setSelectedActivity(selectedActivity) {
    setSettingExistingActivity(true)
    const detailedActivity = await fetchSingleActivity(selectedActivity?.id, mentorProfileData.id)
    handleChangeActivity((prev) => ({ ...detailedActivity, _newActivity: prev?._newActivity }))
    setSettingExistingActivity(false)
  }

  function handleChangeActivityObject(e) {
    handleChangeActivity(e, idx)
  }

  function changeToggleButton(e, newVal) {
    if (newVal !== !!activity?._newActivity) {
      if (!!newVal) {
        setActivity((prev) => ({ ...ACTIVITY_OBJECT, _newActivity: newVal }))
      }
      setActivity((prev) => ({ ...prev, _newActivity: newVal }))
    }
  }

  return (
    <>
      <ActivityContainer main={idx === 0}>
        <div className="d-flex justify-content-between align-items-center pb-3">
          {/* <div className="d-flex justify-content-start align-items-center flex-column">
              <h5>Activity {idx + 1}</h5>{' '}
              {idx === 0 && <Chip size={'small'} label="Main Activity" />}
            </div> */}
          <div>
            {totalActivities > 1 && !disabled && (
              <IconButton onClick={handleDelete}>
                <Delete />
              </IconButton>
            )}
          </div>
        </div>
        <div>
          {!disabled && (
            <div className="d-flex justify-content-center align-items-center my-2">
              <ToggleButtonGroup
                disabled={disabled}
                value={!!activity?._newActivity}
                exclusive
                size="small"
                onChange={changeToggleButton}
                aria-label="use existing or create new activity"
                sx={{ width: '400px' }}
              >
                <ToggleButton sx={{ width: '50%' }} value={true} aria-label="left aligned">
                  Create Activity
                </ToggleButton>
                <ToggleButton sx={{ width: '50%' }} value={false} aria-label="centered">
                  Use Existing Activity
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          )}
          {!activity?._newActivity && !disabled && (
            <div className="d-flex w-100 flex-column gap-1 pb-3">
              <Suspense fallback={<div style={{ padding: '10px' }}>Loading...</div>}>
                <CustomAsyncPaginate
                  menuPortalTarget={document.body}
                  label="Use Existing"
                  value={activity}
                  isLoading={settingExistingActivity}
                  loadOptions={loadActivityOptions}
                  debounceTimeout={1000}
                  getOptionValue={getActivityOptionValue}
                  getOptionLabel={getActivityOptionLabel}
                  additional={{ page: 1 }}
                  required
                  onChange={setSelectedActivity}
                  isDisabled={disabled}
                  components={{ SingleValue: ActivityValue, Option: ActivityOption }}
                  placeholder="Select item..."
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
                />
              </Suspense>
            </div>
          )}
          {(!activity?._newActivity ? !!activity.id : true) && (
            <ActivityComponent
              imageObject={activity}
              setImageObject={setActivity}
              handleChangeObject={handleChangeActivityObject}
              disabled={disabled}
              customOptions={dropDownData}
              view="form"
              day={day}
            >
              {children}
            </ActivityComponent>
          )}
        </div>
        <Suspense fallback={null}>
          <ConfirmationModal {...confirmationModalProps} newImplementation />
        </Suspense>
      </ActivityContainer>
    </>
  )
}

function PlannedActivityPreviewComponent({
  plannedActivity,
  idx,
  masterExpand,
  currentPhase,
  dropDownData,
}) {
  const allActivities = plannedActivity?.otherActivities || []
  const foundCategory = dropDownData?.metaCategories?.find(
    (cat) => cat?.id === allActivities?.[0]?.metaCategoryId,
  )
  return (
    <MyPlanPhaseDayItemContainer>
      <PhaseLegendVerticalDiv legendColor={currentPhase?.legendColor} />
      <UAPTimePlanTitlesAndCheckBoxContainer>
        <UAPDayHeadingContainer>
          <UAPDayHeading>
            <span>Day</span>
            <span>{idx + 1}</span>
          </UAPDayHeading>
        </UAPDayHeadingContainer>
        <UAPDayContentDividerContainer>
          <UAPDayContentDivider />
        </UAPDayContentDividerContainer>
        <UAPDayItemEventsContainer>
          <UAPDayItemEventParent>
            <UAPDayItemEventDetailsContainer>
              <div className={`d-flex flex-column gap-2 w-100`}>
                <UAPDayItemEventDetailsTitle>
                  {allActivities?.[0]?.activityTitle}
                </UAPDayItemEventDetailsTitle>
                {allActivities?.[0]?.customCategoryTitle && (
                  <UAPDayItemEventDetailsSubtitle>
                    <span>
                      {allActivities?.[0]?.customCategoryTitle?.charAt(0)?.toUpperCase() +
                        allActivities?.[0]?.customCategoryTitle?.slice(1)}
                    </span>
                  </UAPDayItemEventDetailsSubtitle>
                )}
              </div>
            </UAPDayItemEventDetailsContainer>
          </UAPDayItemEventParent>
        </UAPDayItemEventsContainer>
      </UAPTimePlanTitlesAndCheckBoxContainer>
    </MyPlanPhaseDayItemContainer>
  )
}

export const ACTIVITY_ADDING_STATES = {
  add_new: 'add new',
  use_existing: 'use existing',
}

function getActivityOptionValue(option) {
  return option?.id
}

function getActivityOptionLabel(option) {
  return option?.activityTitle || option?.title || option?.id
}

function currentPlannedActivityPhase(plannedActivityIndex, allPhases) {
  const foundPhase = allPhases.find(
    (phase) =>
      phase.firstDay <= plannedActivityIndex + 1 && phase.lastDay >= plannedActivityIndex + 1,
  )
  return foundPhase
}
